import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import { reviewApi } from '../../api/reviews';
import api from '../../utils/api';
import TrendCard from './TrendCard';
import MentalHealthAssessment from './MentalHealthAssessment';
import { TrendData } from '../../types/review';

interface EnhancedReviewFormProps {
  clientId: string;
  onSave: () => void;
  onCancel: () => void;
}

interface ClientData {
  firstName: string;
  lastName: string;
  currentCounsellorId: string;
  completedSessions?: number;
  referralTypeDisplayName?: string;
  assessment?: {
    generalConcerns?: string;
    concernsDuration?: string;
    mainObjectives?: string;
  };
  assessmentDateTime?: Date;
}

interface Episode {
  _id: string;
  status: string;
}

const EnhancedReviewForm: React.FC<EnhancedReviewFormProps> = ({
  clientId,
  onSave,
  onCancel
}) => {
  const [client, setClient] = useState<ClientData | null>(null);
  const [episode, setEpisode] = useState<Episode | null>(null);
  const [trendData, setTrendData] = useState<TrendData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    needsBeingMet?: boolean;
    phq9?: boolean;
    gad7?: boolean;
  }>({});

  // Form fields
  const [needsBeingMet, setNeedsBeingMet] = useState('');
  const [phq9Scores, setPhq9Scores] = useState<number[]>(Array(9).fill(undefined));
  const [gad7Scores, setGad7Scores] = useState<number[]>(Array(7).fill(undefined));
  const [progressTowardsObjectives, setProgressTowardsObjectives] = useState('');
  const [recommendedNextSteps, setRecommendedNextSteps] = useState('');
  const [nextStepsNotes, setNextStepsNotes] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  // Calculated totals
  const phq9Total = phq9Scores.filter(s => s !== undefined).reduce((sum, score) => sum + score, 0);
  const gad7Total = gad7Scores.filter(s => s !== undefined).reduce((sum, score) => sum + score, 0);

  useEffect(() => {
    loadData();
  }, [clientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load client data
      const clientResponse = await api.get(`/clients/${clientId}`);
      setClient(clientResponse.data);

      // Load trend data
      try {
        const trends = await reviewApi.getTrendData(clientId);
        setTrendData(trends);
      } catch (trendErr) {
        console.warn('Could not load trend data:', trendErr);
      }

      // Get or create episode
      try {
        const episodesResponse = await api.get(`/episodes?clientId=${clientId}&status=active`);
        const episodes = episodesResponse.data;
        if (episodes && episodes.length > 0) {
          setEpisode(episodes[0]);
        } else {
          const newEpisode = await api.post('/episodes', {
            clientId,
            startDate: new Date(),
            type: 'indefinite',
            status: 'active'
          });
          setEpisode(newEpisode.data);
        }
      } catch (episodeErr) {
        console.error('Error with episodes:', episodeErr);
        setError('Unable to access episode information. Please contact support.');
      }

    } catch (err: any) {
      console.error('Error loading data:', err);
      setError('Failed to load client information');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Reset validation errors
    const errors: typeof validationErrors = {};
    let hasErrors = false;

    // Validation
    if (!needsBeingMet.trim()) {
      errors.needsBeingMet = true;
      hasErrors = true;
    }

    if (phq9Scores.some(s => s === undefined)) {
      errors.phq9 = true;
      hasErrors = true;
    }

    if (gad7Scores.some(s => s === undefined)) {
      errors.gad7 = true;
      hasErrors = true;
    }

    if (hasErrors) {
      setValidationErrors(errors);
      setError('Please complete all required fields');
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!client?.currentCounsellorId || !episode) {
      setError('Missing required data');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await reviewApi.createReview({
        clientId,
        episodeId: episode._id,
        counsellorId: client.currentCounsellorId,
        sessionNumber: client.completedSessions || 0,
        needsBeingMet: needsBeingMet.trim(),
        phq9Scores,
        phq9Total,
        gad7Scores,
        gad7Total,
        progressTowardsObjectives: progressTowardsObjectives.trim() || undefined,
        recommendedNextSteps: recommendedNextSteps || undefined,
        nextStepsNotes: nextStepsNotes.trim() || undefined,
        additionalNotes: additionalNotes.trim() || undefined
      });

      onSave();
    } catch (err: any) {
      setError(err.message || 'Failed to save review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!client) {
    return (
      <Alert severity="error">
        Failed to load client information
      </Alert>
    );
  }

  return (
    <Box sx={{ pt: 2 }}>
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            bgcolor: 'rgba(211, 47, 47, 0.1)',
            color: '#f44336',
            border: '1px solid rgba(211, 47, 47, 0.3)'
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Client Information */}
      <Box sx={{ mb: 4, p: 3, bgcolor: 'rgba(255, 255, 255, 0.03)', borderRadius: 2 }}>
        <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 600, mb: 1 }}>
          {client.firstName} {client.lastName}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Session #{client.completedSessions || 0} Review
        </Typography>
        {client.referralTypeDisplayName && (
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Referral: {client.referralTypeDisplayName}
          </Typography>
        )}
      </Box>

      {/* Initial Assessment Information */}
      {client.assessment && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 600, mb: 2 }}>
            Initial Assessment
          </Typography>
          <Box className="glass-card p-4" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {client.assessment.generalConcerns && (
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 600 }}>
                  General Concerns:
                </Typography>
                <Typography variant="body2" className="theme-text-primary">
                  {client.assessment.generalConcerns}
                </Typography>
              </Box>
            )}
            {client.assessment.concernsDuration && (
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 600 }}>
                  Duration:
                </Typography>
                <Typography variant="body2" className="theme-text-primary">
                  {client.assessment.concernsDuration}
                </Typography>
              </Box>
            )}
            {client.assessment.mainObjectives && (
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 600 }}>
                  What they want from counselling:
                </Typography>
                <Typography variant="body2" className="theme-text-primary">
                  {client.assessment.mainObjectives}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Mental Health Trends */}
      {trendData && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 600, mb: 2 }}>
            Mental Health Trends
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TrendCard
              title="PHQ-9 (Depression)"
              initialScore={trendData.initialAssessment?.phq9Total}
              previousScore={trendData.previousReview?.phq9Total}
              currentScore={phq9Total > 0 ? phq9Total : undefined}
              maxScore={27}
            />
            <TrendCard
              title="GAD-7 (Anxiety)"
              initialScore={trendData.initialAssessment?.gad7Total}
              previousScore={trendData.previousReview?.gad7Total}
              currentScore={gad7Total > 0 ? gad7Total : undefined}
              maxScore={21}
            />
          </Box>
        </Box>
      )}

      {/* PHQ-9 Assessment */}
      <Box sx={{ 
        mb: 4,
        p: 2,
        borderRadius: '8px',
        border: validationErrors.phq9 ? '2px solid #f44336' : 'none',
        backgroundColor: validationErrors.phq9 ? 'rgba(244, 67, 54, 0.05)' : 'transparent'
      }}>
        <MentalHealthAssessment
          type="PHQ-9"
          scores={phq9Scores}
          onChange={(scores) => {
            setPhq9Scores(scores);
            if (validationErrors.phq9 && scores.every(s => s !== undefined)) {
              setValidationErrors({ ...validationErrors, phq9: false });
              setError(null);
            }
          }}
          total={phq9Total}
        />
        {validationErrors.phq9 && (
          <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block', fontWeight: 600 }}>
            Please complete all PHQ-9 questions
          </Typography>
        )}
      </Box>

      {/* GAD-7 Assessment */}
      <Box sx={{ 
        mb: 4,
        p: 2,
        borderRadius: '8px',
        border: validationErrors.gad7 ? '2px solid #f44336' : 'none',
        backgroundColor: validationErrors.gad7 ? 'rgba(244, 67, 54, 0.05)' : 'transparent'
      }}>
        <MentalHealthAssessment
          type="GAD-7"
          scores={gad7Scores}
          onChange={(scores) => {
            setGad7Scores(scores);
            if (validationErrors.gad7 && scores.every(s => s !== undefined)) {
              setValidationErrors({ ...validationErrors, gad7: false });
              setError(null);
            }
          }}
          total={gad7Total}
        />
        {validationErrors.gad7 && (
          <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block', fontWeight: 600 }}>
            Please complete all GAD-7 questions
          </Typography>
        )}
      </Box>

      {/* Review Questions */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 600, mb: 3 }}>
          Review Questions
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Needs Being Met */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Are the client's needs being met? *"
            value={needsBeingMet}
            onChange={(e) => {
              setNeedsBeingMet(e.target.value);
              if (validationErrors.needsBeingMet && e.target.value.trim()) {
                setValidationErrors({ ...validationErrors, needsBeingMet: false });
                setError(null);
              }
            }}
            placeholder="Please provide your assessment..."
            required
            error={validationErrors.needsBeingMet}
            helperText={validationErrors.needsBeingMet ? 'This field is required' : ''}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                color: 'rgba(255, 255, 255, 0.9)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#667eea' },
                '&.Mui-error fieldset': { borderColor: '#f44336' }
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#667eea' },
              '& .MuiInputLabel-root.Mui-error': { color: '#f44336' },
              '& .MuiFormHelperText-root': { color: '#f44336' }
            }}
          />

          {/* Progress Towards Objectives */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Progress towards objectives"
            value={progressTowardsObjectives}
            onChange={(e) => setProgressTowardsObjectives(e.target.value)}
            placeholder="How is the client progressing towards their stated objectives?"
            sx={{ 
              '& .MuiOutlinedInput-root': {
                color: 'rgba(255, 255, 255, 0.9)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#667eea' }
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#667eea' }
            }}
          />

          {/* Recommended Next Steps */}
          <FormControl fullWidth>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Recommended next steps</InputLabel>
            <Select
              value={recommendedNextSteps}
              onChange={(e) => setRecommendedNextSteps(e.target.value)}
              label="Recommended next steps"
              sx={{
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
              }}
            >
              <MenuItem value="">Select...</MenuItem>
              <MenuItem value="continue">Continue current approach</MenuItem>
              <MenuItem value="adjust">Adjust approach</MenuItem>
              <MenuItem value="referral">Consider referral</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          {/* Next Steps Notes */}
          {recommendedNextSteps && (
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Next steps details"
              value={nextStepsNotes}
              onChange={(e) => setNextStepsNotes(e.target.value)}
              placeholder="Provide additional details about the recommended next steps..."
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  color: 'rgba(255, 255, 255, 0.9)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea' }
                },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#667eea' }
              }}
            />
          )}

          {/* Additional Notes */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Additional notes"
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            placeholder="Any other observations or notes..."
            sx={{ 
              '& .MuiOutlinedInput-root': {
                color: 'rgba(255, 255, 255, 0.9)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: '#667eea' }
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#667eea' }
            }}
          />
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
        <Button 
          onClick={onCancel}
          variant="outlined"
          disabled={submitting}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1,
            borderColor: 'rgba(255, 255, 255, 0.3)',
            color: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a4293 100%)',
              boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)'
            },
            '&:disabled': {
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'rgba(255, 255, 255, 0.5)'
            }
          }}
        >
          {submitting ? 'Saving...' : 'Save Review'}
        </Button>
      </Box>
    </Box>
  );
};

export default EnhancedReviewForm;
